/**
 * Does this customer still have to go through the payment step?
 *
 * One decision, in one place, because it is made from four of them —
 * the Contact→next routing, the PHD-studio variant, the card-state
 * mismatch retry, and the progress bar's flow array. They agreed with
 * each other by being written the same way, which held right up until
 * one of them needed a new clause.
 *
 * The new clause is `alreadyPaid`. A PaymentIntent-first payment leaves
 * nothing on the cart that the widget can see — no attached card, no
 * `paymentAttached` — so a customer who has already paid and come back
 * round the loop (a sniped slot, a failed booking) looks identical to
 * one who has not, and the step opens again. That is a second Element,
 * a second intent, and a second charge for one booking.
 *
 * `alreadyPaid` is a DECISION the server made, not a figure to compare.
 * An earlier version shipped the figure and compared it here with `>=`
 * while checkout compared with `===`; every input in that gap was a
 * customer who skipped the step and then met a refusal with no route
 * back to it — worse than the double charge it replaced. The server now
 * answers both questions with one call, so the two cannot drift.
 *
 * The flag is sent only where it can mean something (the deferred
 * Payment Element), so everywhere else it is absent, the clause is
 * inert, and the answer is the one this rule has always given.
 */

export interface PaymentStepQuote {
  enabled?: boolean;
  amountCents?: number;
  cardOnFile?: boolean;
  allowPayInFull?: boolean;
  fullAmountCents?: number;
  /**
   * The server has settled money on this cart that checkout will accept
   * for exactly this amount. Server-decided; see the note above on why
   * this is not a figure.
   */
  alreadyPaid?: boolean;
}

export function paymentStepNeeded(deposit: PaymentStepQuote | null | undefined): boolean {
  if (!deposit?.enabled) return false;

  // Covered — the server has money it will accept for this exact
  // amount. Anything less than that (a part payment, an overpayment, a
  // partly refunded one) is NOT covered and comes back false, which
  // keeps the step open rather than routing into a refusal.
  if (deposit.alreadyPaid === true) return false;

  const owed = deposit.amountCents ?? 0;
  return (
    owed > 0 ||
    // $0 card-on-file: nothing is charged today, but a card is still
    // required, so the step stands.
    deposit.cardOnFile === true ||
    // A tenant that takes no deposit can still let a client prepay;
    // their opening quote is $0 and the pay-in-full option lives behind
    // this step.
    (deposit.allowPayInFull === true && (deposit.fullAmountCents ?? 0) > 0)
  );
}
